<template>
  <div>
    <div class="tw-w-full tw-mt-4">
      <div
        class="tw-cursor-pointer dfs-container"
        :class="{ 'tw-opacity-25': !enabled }"
        @click.stop="setMode('PickUp')"
      >
        <div class="dfs-left">
          <div class="tw-flex tw-w-full">
            <stihl-icon
              class="tw-w-4 tw-h-4 tw-mt-1 tw-mr-2"
              name="map-marker"
            />
            <div>
              <div class="tw-font-sans tw-text-lg tw-font-bold">
                Pick Up In Store
              </div>
              <div class="tw-font-sans tw-text-sm tw-font-bold tw-uppercase">
                FREE
              </div>
            </div>
          </div>
        </div>
        <div class="dfs-right">
          <div class="tw-flex tw-justify-center">
            <input
              v-model="deliveryType"
              type="radio"
              name="deliveryType"
              value="PickUp"
              class="accent-black"
              :disabled="!enabled"
              data-test="pickup-radio"
              aria-label="Pick Up In Store"
            >
          </div>
        </div>
      </div>
      <div
        class="dfs-container"
        :class="{ 'tw-opacity-25': !showDeliveryOptions || !enabled }"
      >
        <div class="dfs-left">
          <delivery-type-selector-info
            noun="Delivery"
            verb="deliver"
            :dealer-name="dealerName"
            :enabled="showDeliveryOptions && enabled"
            :has-located="hasLocated"
            icon="delivery-truck"
            :rate="matchingDfsRate"
            title="Deliver From Store"
            :zone="matchingDfsZone"
            :address="address"
            @setAddress="showDeliveryPanel = true"
          />
        </div>
        <div
          class="tw-cursor-pointer dfs-right"
          @click.stop="doLocalDelivery(false)"
        >
          <div class="tw-flex tw-justify-center">
            <input
              v-model="deliveryType"
              type="radio"
              name="deliveryType"
              value="LocalDelivery"
              class="accent-black"
              :disabled="!showDeliveryOptions || !enabled"
              data-test="delivery-radio"
              aria-label="Deliver From Store"
              @click="doLocalDelivery(false)"
            >
          </div>
        </div>
      </div>
      <div
        v-if="systemShippable"
        class="dfs-container"
        :class="{ 'tw-opacity-25': !showShippingOptions || !enabled }"
      >
        <div class="dfs-left">
          <delivery-type-selector-info
            noun="Shipping"
            verb="ship"
            :dealer-name="dealerName"
            :enabled="showShippingOptions && enabled"
            :has-located="hasLocated"
            icon="house-chimney"
            :rate="matchingS2hRate"
            title="Ship To Home"
            :zone="matchingS2hZone"
            :address="address"
            @setAddress="showDeliveryPanel = true"
          />
        </div>
        <div
          class="tw-cursor-pointer dfs-right"
          @click.stop="doShipToHome(false)"
        >
          <div class="tw-flex tw-justify-center">
            <input
              v-model="deliveryType"
              type="radio"
              name="deliveryType"
              value="ShipToHome"
              class="accent-black"
              :disabled="!showShippingOptions || !enabled"
              data-test="shipping-radio"
              aria-label="Ship To Home"
              @click="doShipToHome(false)"
            >
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showDeliveryPanel"
      class="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-black tw-opacity-70 tw-z-overlay"
    />
    <div
      class="tw-fixed tw-top-0 tw-right-0 tw-h-screen tw-w-full md_tw-max-w-xl tw-bg-white tw-z-top tw-pt-20 tw-p-11 tw-transform tw-ease-in-out tw-transition-all tw-overflow-y-auto tw-z-modal"
      :class="showDeliveryPanel ? 'tw-translate-x-0' : 'tw-translate-x-full'"
    >
      <div
        class="tw-absolute tw-top-0 tw-right-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-bg-black button-default tw-w-11 tw-h-11"
        @click="setDeliveryPanel(false)"
      >
        <stihl-icon
          name="close"
          class="tw-w-4 tw-h-4"
        />
      </div>
      <div class="tw-pb-4 tw-text-xl tw-font-title">
        Set My Address
      </div>
      <div class="tw-h-full tw-overflow-y-auto">
        <div v-if="loading">
          <img
            src="https://assets.stihldealer.net/common/spinner.svg"
            width="20"
            alt="loading icon"
            data-test="spinner-icon"
          >
        </div>
        <div
          v-show="!loading"
          class="tw-w-full"
        >
          <div class="tw-flex tw-items-center tw-w-full tw-h-12">
            <GmapAutocomplete 
              :value="googleAddress"
              placeholder="Street address, city, state and ZIP"
              :fields="gmap.fields"
              :types="gmap.types"
              :component-restrictions="gmap.componentRestrictions"
              class="tw-w-full tw-h-full tw-p-1 tw-pl-3 tw-pr-8 tw-text-sm tw-border tw-border-b tw-rounded"
              @place_changed="setPlace"
            />
            <span
              class="tw-px-1 -tw-ml-8 tw-cursor-pointer icon icon-stihl-close"
              @click="clearAddress()"
            />
          </div>
          <div class="tw-mt-1 tw-text-xs">
            e.g., 123 Your Street, Virginia Beach, VA 12345
          </div>
          <div class="tw-mt-12">
            <div v-if="hasLocated">
              <div
                v-if="showDeliveryOptions"
                class="tw-mt-4"
                :class="showDeliveryInfo ? '' : 'tw-text-red'"
              >
                <div
                  class="tw-font-bold"
                  data-test="dealer-delivers-message"
                >
                  {{ dealerName }} <span v-if="showDeliveryInfo">delivers</span><span v-if="!showDeliveryInfo">does not deliver</span> to this address.
                </div>
                <div
                  v-if="matchingDfsZone"
                  class="tw-p-2 tw-my-2 tw-bg-gray-light"
                >
                  <div class="tw-my-1 tw-font-bold">
                    Additional information for your delivery zone:
                  </div>
                  <div>
                    {{ matchingDfsZone.deliveryInformation }}
                  </div>
                </div>
              </div>
              <div
                v-if="showShippingOptions"
                class="tw-mt-4"
                :class="showShippingInfo ? '' : 'tw-text-red'"
              >
                <div
                  class="tw-font-bold"
                  data-test="dealer-ships-message"
                >
                  {{ dealerName }} <span v-if="showShippingInfo">ships</span><span v-if="!showShippingInfo">does not ship</span> to this address.
                </div>
                <div
                  v-if="matchingS2hZone"
                  class="tw-p-2 tw-my-2 tw-bg-gray-light"
                >
                  <div class="tw-my-1 tw-font-bold">
                    Additional information for your shipping zone:
                  </div>
                  <div>
                    {{ matchingS2hZone.deliveryInformation }}
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="tw-w-full tw-mt-4 button-default tw-p-5 tw-text-white tw-font-title tw-inline-block"
              @click="setDeliveryPanel(false)"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import StihlIcon from './StihlIcon.vue';
import DeliveryTypeSelectorInfo from './DeliveryTypeSelectorInfo.vue'
import { gmapApi } from 'vue2-google-maps'
import api from '@/api/index'

const LOCAL_DELIVERY = 'LocalDelivery'
const PICK_UP = 'PickUp'
const SHIP_TO_HOME = 'ShipToHome'

export default {  
  components: {
    StihlIcon,
    DeliveryTypeSelectorInfo    
  },
  props: {
    variant: { 
      type: Object,
      required: true
    },
    deliverySettings: {
      type: Object,
      required: true
    },
    shippingSettings: {
      type: Object,
      required: true
    },
    variantDeliveryInfo: {
      type: Array,
      required: true
    },
    quantity: {
      type: Number,
      required: true
    },
    dealerApiUrl: {
      type: String,
      required: true
    },
    dealerId: {
      type: Number,
      required: true
    },
    dealerName: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    enabled: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showDeliveryPanel: false,
      isLocating: false,
      hasLocated: false,
      hasDeliverableAddress: false,
      hasShippableAddress: false,
      address: null,
      loading: false,
      googleAddress: '',
      zoneInfo: {},
      gmap: {
        types: ['address'],
        fields: ['formatted_address', 'geometry', 'address_components'],
        componentRestrictions: { country: ['us', 'pr', 'vi'] }
      },
    }
  },
  computed: {
    deliveryType: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('update', val)
      }
    },
    showDeliveryOptions() {
      if (!this.deliverySettings.enabled) {
        return false
      }

      const varSet = this.variantDeliveryInfo.find(t => t.attributeId === this.variant.id)

      if (!varSet) {
        return false
      }

      return varSet.isDeliverable && this.deliverySettings.enabled
    },
    showShippingOptions() {
      if (!this.shippingSettings.enabled) {
        return false
      }
      const varSet = this.variantDeliveryInfo.find(t => t.attributeId === this.variant.id)
      if (!varSet) {
        return false
      }
      return varSet.isShippable && this.shippingSettings.enabled
    },
    systemShippable() {
      const varSet = this.variantDeliveryInfo.find(t => t.attributeId === this.variant.id)
      if (!varSet) {
        return false
      }
      return varSet.systemShippable
    },
    matchingDfsRate () {
      if (!this.matchingDfsZone || !this.matchingDfsZone.rates || this.matchingDfsZone.rates.length < 1) {
        return null
      }

      const matchingRate = this.getMatchingRate(this.matchingDfsZone.rates)

      if (matchingRate != null) {
        return matchingRate
      }

      // There is no matching rate. This usually only happens when price < rate, so return the lowest rate
      return this.matchingDfsZone.rates[0]
    },
    matchingDfsZone () {
      if (this.zoneInfo && this.zoneInfo.dfsZone) {
        return this.zoneInfo.dfsZone
      }
      return null
    },
    matchingS2hZone () {
      if (this.zoneInfo && this.zoneInfo.s2hZone) {
        return this.zoneInfo.s2hZone
      }
      return null
    },
    matchingS2hRate () {
      if (!this.matchingS2hZone || !this.matchingS2hZone.rates || this.matchingS2hZone.rates.length < 1) {
        return null
      }

      const matchingRate = this.getMatchingRate(this.matchingS2hZone.rates)

      if (matchingRate != null) {
        return matchingRate
      }

      // There is no matching rate. This usually only happens when price < rate, so return the lowest rate
      return this.matchingS2hZone.rates[0]
    },
    smallestRate() {
      return this.matchingZone.rates[0]
    },
    showDeliveryInfo () {
      return this.hasLocated && this.matchingDfsZone != null && this.showDeliveryOptions
    },
    showShippingInfo () {
      return this.hasLocated && this.matchingS2hZone != null && this.showShippingOptions
    },
    google: gmapApi
  },
  watch: {
    variantDeliveryInfo: {
      handler() {
        this.loadAddress()
      },
      deep: true
    },
    variant () {
      if (!this.showDeliveryOptions) {
        this.setMode(PICK_UP)
      }
    }
  },
  mounted () {
    this.loadAddress()
    this.$emit('loaded')    
  },
  methods: {
    setMode (mode) {
      this.$emit('update', mode)
    },
    doLocalDelivery () {
      if (!this.showDeliveryOptions) {
        return
      }

      if (this.hasDeliverableAddress && this.hasLocated) {
        this.setMode(LOCAL_DELIVERY)
        return
      }

      this.setDeliveryPanel(true)
    },
    doShipToHome () {
      if (!this.showShippingOptions) {
        return
      }

      if (this.hasShippableAddress && this.hasLocated) {
        this.setMode(SHIP_TO_HOME)
        return
      }

      this.setDeliveryPanel(true)
    },
    getStoredAddress () {
      const storage = localStorage.getItem('Delivery.Address')
      if (storage) {
        try {
          const address = JSON.parse(storage)
          return address
        } catch { }
      }
      return {} 
    },
    getStoredZone () {
      const storage = sessionStorage.getItem('Delivery.Zone')
      if (storage) {
        try {
          const zoneInfo = JSON.parse(storage)
          return zoneInfo
        } catch { }
      }
      return null
    },
    loadAddress () {
      const address = this.getStoredAddress()
      if (address && address.lat && address.lng) {
        this.address = `${address.address1}${address.address2 ? ' ' + address.address2 : ''}, ${address.city}, ${address.state} ${address.zip}`
        this.googleAddress = this.address
        this.$emit('setAddress')
        this.getMatchingZone(address)
      }
    },
    saveZone (zoneInfo) {
      // we store zone in session so it doesn't get stale. will refresh on each initial load
      this.zoneInfo = zoneInfo
      sessionStorage.setItem('Delivery.Zone', JSON.stringify(zoneInfo))
    },
    clearZone () {
      this.zoneInfo = null
      sessionStorage.removeItem('Delivery.Zone')
    },
    saveAddress (addressObject) {
      const address = JSON.stringify(addressObject)
      localStorage.setItem('Delivery.Address', address)
    },
    setDeliveryPanel (val) {
      if (!val) {
        if (this.deliveryType === LOCAL_DELIVERY && !this.hasDeliverableAddress) {
          this.deliveryType = PICK_UP
        }

        if (this.deliveryType === SHIP_TO_HOME && !this.hasShippableAddress) {
          this.deliveryType = PICK_UP
        }
      }

      this.showDeliveryPanel = val
    },
    async setPlace (place) {
      if (!place.address_components) {
        this.hasDeliverableAddress = false
        this.hasShippableAddress = false
        return
      }

      this.loading = true

      const addressComps = place.address_components
      const subpremise = addressComps.find(t => t.types.includes('subpremise'))
      const streetNumber = addressComps.find(t => t.types.includes('street_number'))
      const street = addressComps.find(t => t.types.includes('route'))
      const sublocality = addressComps.find(t => t.types.includes('sublocality'))
      const city = addressComps.find(t => t.types.includes('locality'))
      const state = addressComps.find(t => t.types.includes('administrative_area_level_1'))
      const zip = addressComps.find(t => t.types.includes('postal_code'))

      if (!street || (!city && !sublocality) || !state || !zip) {
        this.loading = false
        this.hasDeliverableAddress = false
        this.hasShippableAddress = false
        return
      }

      const unifiedCity = !!sublocality && !!city
        ? `${sublocality.long_name} ${city.long_name}`
        : sublocality ? sublocality.long_name : city?.long_name || ''

      const address = {
        address1: `${streetNumber ? streetNumber.long_name + ' ' : ''}${street.long_name}`,
        address2: subpremise ? subpremise.long_name : null,
        city: unifiedCity,
        state: state.short_name,
        zip: zip.long_name,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      }
      this.address = place.formatted_address
      this.googleAddress = place.formatted_address

      await this.getMatchingZone(address)
      this.loading = false
    },
    async getMatchingZone (address) {
      if (this.isLocating) {
        return
      }
      
      this.saveAddress(address)
      this.$emit('setAddress')
      const storedZoneInfo = this.getStoredZone()
      
      if (!storedZoneInfo || storedZoneInfo?.lat !== address.lat || storedZoneInfo?.lng !== address.lng || storedZoneInfo.dealerId !== this.dealerId) {
        // if our address or dealer has changed, or we don't have a stored zone, we need to do a lookup
        this.zoneInfo = null
        const price = this.variant.price * this.quantity
        this.isLocating = true
        try {
          const response = await api.getDeliveryZone(this.dealerApiUrl, this.dealerId, address.zip, address.state, address.lat, address.lng, price)
          if (response) {
            this.saveZone({
              dealerId: this.dealerId,
              lat: address.lat,
              lng: address.lng,
              dfsZone: response.localDelivery.zone,
              s2hZone: response.shipping.zone
            })
          } else {
            // if we got no zone, our address is not deliverable, clear the old zone
            this.clearZone()
          }
        } catch (err) {
          this.clearZone()
        } finally {
          this.isLocating = false
        }
      } else {
        this.zoneInfo = storedZoneInfo
      }
      this.hasLocated = true
      if (this.matchingDfsZone != null) {
        // sort the rates properly just in case
        this.matchingDfsZone.rates.sort((a, b) => { return a.minimumOrderTotal - b.minimumOrderTotal })
        this.hasDeliverableAddress = true
        if (this.deliveryType === LOCAL_DELIVERY) {
          this.setMode(LOCAL_DELIVERY)
        }
      } else {
        this.hasDeliverableAddress = false
      }
      if (this.matchingS2hZone != null) {
        // sort the rates properly just in case
        this.matchingS2hZone.rates.sort((a, b) => { return a.minimumOrderTotal - b.minimumOrderTotal })
        this.hasShippableAddress = true
        if (this.deliveryType === SHIP_TO_HOME) {
          this.setMode(SHIP_TO_HOME)
        }
      } else {
        this.hasShippableAddress = false
      }
      if (!this.matchingDfsZone && !this.matchingS2hZone) {
        this.deliveryType = PICK_UP
        this.setMode(PICK_UP)
        this.hasDeliverableAddress = false
        this.hasShippableAddress = false
      }
    },
    getMatchingRate (rates) {
      let matchingRate = null

      for (const rateIndex in rates) {
        const rate = rates[rateIndex]
        if (rate.minimumOrderTotal < this.variant.price * this.quantity) {
          matchingRate = rate
        }
      }

      return matchingRate
    },
    clearAddress () {
      this.googleAddress = ''
    }
  },
}
</script>
<style scoped>
  .dfs-container {
    height: 100%;
    min-height: 85px;
    border: 1px solid black;
    padding: 16px 0 0 12px;
    margin-bottom: 4px;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: row;
    position: relative;
    background-color: white;
  }
  .selected {
    border: 2px solid black;
  }
  .dfs-left {
    width: calc(100% - 52px);
    height: 100%;
    padding-right: 2px;
  }
  .dfs-right {
    width: 52px;
    height: 100%;
    margin-top: -16px;
    padding-top: 16px;
    background-color: #eee;
    position: absolute;
    right: 0;
  }  
  .accent-black {
    accent-color: #000;
  }
</style>